import React, { useState, useEffect } from 'react';
import { api, uploadMedia, deleteUploadedMedia, resolveMediaUrl } from '../lib/api';
import { useApp } from '../context/AppContext';
import { LANGUAGES } from '../i18n';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Settings, Globe, EyeOff, LogOut, Camera } from 'lucide-react';
import { toast } from 'sonner';

export default function SettingsPage() {
  const { user, t, lang, setLang, logout, refreshUser } = useApp();
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [username, setUsername] = useState(user?.username || '');
  const [weight, setWeight] = useState(user?.weight_kg ?? '');
  const [height, setHeight] = useState(user?.height_cm ?? '');
  const [goal, setGoal] = useState(user?.training_goal || '');
  const [avatar, setAvatar] = useState(user?.avatar || '');
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [anon, setAnon] = useState(user?.anonymous_mode || false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!user) return;
    setFullName(user.full_name || ''); setBio(user.bio || ''); setUsername(user.username || '');
    setWeight(user.weight_kg ?? ''); setHeight(user.height_cm ?? ''); setGoal(user.training_goal || '');
    setAvatar(user.avatar || ''); setAnon(Boolean(user.anonymous_mode));
  }, [user]);
  useEffect(() => () => {
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
  }, [avatarPreview]);

  const chooseAvatar = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error(t('chooseImage')); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error(t('profileImageTooLarge')); return; }
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
    toast.success(t('avatarReady'));
    event.target.value = '';
  };

  const save = async () => {
    setSaving(true);
    let uploadedAvatar = '';
    try {
      if (avatarFile) {
        setUploading(true);
        uploadedAvatar = (await uploadMedia(avatarFile)).url;
      }
      await api.put('/users/me', { username, full_name: fullName, bio, anonymous_mode: anon,
        weight_kg: weight === '' ? null : Number(weight), height_cm: height === '' ? null : Number(height),
        training_goal: goal, ...((uploadedAvatar || avatar) ? { avatar: uploadedAvatar || avatar } : {}) });
      if (avatarPreview) URL.revokeObjectURL(avatarPreview);
      if (uploadedAvatar) setAvatar(uploadedAvatar);
      setAvatarFile(null);
      setAvatarPreview('');
      await refreshUser();
      toast.success(t('settingsSaved'));
    } catch (error) {
      if (uploadedAvatar) await deleteUploadedMedia(uploadedAvatar).catch(() => {});
      toast.error(error.response?.data?.detail || t('connectionError'));
    }
    setUploading(false);
    setSaving(false);
  };

  return (
    <div className="max-w-xl mx-auto px-3 sm:px-4 py-4">
      <div className="flex items-center gap-2 mb-5">
        <Settings size={20} className="text-primary" />
        <h1 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{t('settings')}</h1>
      </div>

      <div className="space-y-5">
        {/* Profile */}
         <div className="bg-card rounded-xl border border-border p-5 elev-1 space-y-4">
          <h3 className="text-sm font-semibold">{t('profile')}</h3>
           <div className="flex items-center gap-4 pb-2">
             <div className="relative">
               {(avatarPreview || avatar) ? <img data-testid="settings-avatar-preview" src={avatarPreview || resolveMediaUrl(avatar)} alt="" className="h-20 w-20 rounded-full object-cover ring-2 ring-primary/25" /> : <div className="h-20 w-20 rounded-full bg-secondary flex items-center justify-center text-2xl font-semibold">{(username || '?')[0].toUpperCase()}</div>}
               <label htmlFor="settings-avatar-input" data-testid="settings-avatar-upload-label" className="absolute -bottom-1 -end-1 rounded-full bg-primary text-primary-foreground p-2 cursor-pointer"><Camera size={14} /></label>
               <input id="settings-avatar-input" data-testid="settings-avatar-input" type="file" accept="image/*" className="sr-only" onChange={chooseAvatar} />
             </div>
             <div><p className="text-sm font-semibold">{t('profilePhoto')}</p><p className="text-xs text-muted-foreground">{uploading ? t('uploading') : t('imageFormats')}</p></div>
           </div>
           <div className="space-y-1.5">
             <Label className="text-xs">{t('username')}</Label>
             <Input data-testid="settings-username-input" value={username} onChange={(e) => setUsername(e.target.value)} />
           </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t('fullName')}</Label>
            <Input data-testid="settings-fullname-input" value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </div>
           <div className="grid grid-cols-2 gap-3">
             <div className="space-y-1.5"><Label className="text-xs">{t('weight')}</Label><Input data-testid="settings-weight-input" type="number" min="20" max="400" value={weight} onChange={(e) => setWeight(e.target.value)} /></div>
             <div className="space-y-1.5"><Label className="text-xs">{t('height')}</Label><Input data-testid="settings-height-input" type="number" min="80" max="260" value={height} onChange={(e) => setHeight(e.target.value)} /></div>
           </div>
           <div className="space-y-1.5"><Label className="text-xs">{t('trainingGoal')}</Label><Input data-testid="settings-goal-input" value={goal} onChange={(e) => setGoal(e.target.value)} placeholder={t('goalPlaceholder')} /></div>
          <div className="space-y-1.5">
             <Label className="text-xs">{t('bio')}</Label>
             <Textarea data-testid="settings-bio-input" value={bio} onChange={(e) => setBio(e.target.value)} placeholder={t('bioPlaceholder')} maxLength={500} />
          </div>
        </div>

        {/* Language */}
        <div className="bg-card rounded-xl border border-border p-5 elev-1">
          <h3 className="text-sm font-semibold flex items-center gap-2 mb-1"><Globe size={15} className="text-[hsl(205,100%,56%)]" /> {t('language')}</h3>
          <p className="text-xs text-muted-foreground mb-3">{t('languageDescription')}</p>
          <Select value={lang} onValueChange={setLang}>
            <SelectTrigger data-testid="settings-language-select" className="w-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              {LANGUAGES.map((l) => <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Privacy */}
        <div className="bg-card rounded-xl border border-border p-5 elev-1">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold flex items-center gap-2"><EyeOff size={15} className="text-muted-foreground" /> {t('anonymousMode')}</h3>
               <p className="text-xs text-muted-foreground mt-1">{t('anonymousDescription')}</p>
            </div>
            <Switch data-testid="settings-anonymous-toggle" checked={anon} onCheckedChange={setAnon} />
          </div>
        </div>

        <Button data-testid="settings-save-button" onClick={save} disabled={saving} className="w-full font-semibold">
          {saving ? t('loading') : t('save')}
        </Button>

        <Button data-testid="settings-logout-button" variant="outline" onClick={logout} className="w-full border-destructive/40 text-destructive hover:bg-destructive/10">
          <LogOut size={15} className="me-2" /> {t('logout')}
        </Button>
      </div>
    </div>
  );
}
